import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { api, onSignOut, tokenStore } from './api';
import type { Company, Tokens, User } from '../types';

interface SessionPayload {
  user: User;
  company: Company | null;
  tokens: Tokens;
  nextStep?: string;
}

interface AuthValue {
  user: User | null;
  company: Company | null;
  /** Undefined while the stored token is being checked on first paint. */
  loading: boolean;
  /** True when a platform admin is viewing a company's console. */
  impersonating: boolean;
  signIn: (email: string, password: string) => Promise<SessionPayload>;
  signOut: () => Promise<void>;
  can: (...roles: User['role'][]) => boolean;
  /** Re-fetch the signed-in user and company (e.g. after a logo change). */
  refresh: () => Promise<void>;
  /** Enter a company's console as though you were its admin. */
  impersonate: (companyId: string) => Promise<void>;
  /** Leave the impersonated company and return to the platform console. */
  stopImpersonating: () => Promise<void>;
}

const PLATFORM_ACCESS = 'stz.platform.access';
const PLATFORM_REFRESH = 'stz.platform.refresh';

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(
    () => localStorage.getItem(PLATFORM_ACCESS) !== null,
  );

  // A refresh that fails anywhere in the app lands here.
  useEffect(() => onSignOut(() => {
    setUser(null);
    setCompany(null);
  }), []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!tokenStore.access) {
        setLoading(false);
        return;
      }
      try {
        const me = await api.get<{ user: User; company: Company | null; impersonating?: boolean }>('/auth/me');
        if (!cancelled) {
          setUser(me.user);
          setCompany(me.company);
          setImpersonating(me.impersonating === true);
        }
      } catch {
        if (!cancelled) tokenStore.clear();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const session = await api.anon<SessionPayload>('/auth/login', {
      email,
      password,
      device: { platform: 'web', name: 'Admin console' },
    });
    tokenStore.set(session.tokens.accessToken, session.tokens.refreshToken);
    setUser(session.user);
    setCompany(session.company);
    return session;
  }, []);

  const signOut = useCallback(async () => {
    const refreshToken = tokenStore.refresh;
    try {
      if (refreshToken) await api.post('/auth/logout', { refreshToken });
    } catch {
      // Signing out locally matters more than the server round-trip.
    }
    tokenStore.clear();
    // Also clear any stashed platform tokens.
    localStorage.removeItem(PLATFORM_ACCESS);
    localStorage.removeItem(PLATFORM_REFRESH);
    setUser(null);
    setCompany(null);
    setImpersonating(false);
  }, []);

  const can = useCallback(
    (...roles: User['role'][]) => Boolean(user && roles.includes(user.role)),
    [user],
  );

  const refresh = useCallback(async () => {
    if (!tokenStore.access) return;
    const me = await api.get<{ user: User; company: Company | null; impersonating?: boolean }>('/auth/me');
    setUser(me.user);
    setCompany(me.company);
    setImpersonating(me.impersonating === true);
  }, []);

  const impersonate = useCallback(async (companyId: string) => {
    // Stash the platform admin's tokens so we can come back.
    const platformAccess = tokenStore.access;
    const platformRefresh = tokenStore.refresh;
    if (platformAccess && platformRefresh) {
      localStorage.setItem(PLATFORM_ACCESS, platformAccess);
      localStorage.setItem(PLATFORM_REFRESH, platformRefresh);
    }

    const session = await api.post<SessionPayload & { impersonating?: boolean }>(
      '/auth/impersonate',
      { companyId },
    );
    tokenStore.set(session.tokens.accessToken, session.tokens.refreshToken);
    setUser(session.user);
    setCompany(session.company);
    setImpersonating(true);
  }, []);

  const stopImpersonating = useCallback(async () => {
    const platformAccess = localStorage.getItem(PLATFORM_ACCESS);
    const platformRefresh = localStorage.getItem(PLATFORM_REFRESH);

    localStorage.removeItem(PLATFORM_ACCESS);
    localStorage.removeItem(PLATFORM_REFRESH);

    if (platformAccess && platformRefresh) {
      tokenStore.set(platformAccess, platformRefresh);
    }

    // Re-fetch to get the platform admin's own identity back.
    setImpersonating(false);
    try {
      const me = await api.get<{ user: User; company: Company | null }>('/auth/me');
      setUser(me.user);
      setCompany(me.company);
    } catch {
      tokenStore.clear();
      setUser(null);
      setCompany(null);
    }
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ user, company, loading, impersonating, signIn, signOut, can, refresh, impersonate, stopImpersonating }),
    [user, company, loading, impersonating, signIn, signOut, can, refresh, impersonate, stopImpersonating],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
